import { useRef, useState } from 'react';
import { CaptureAvatar } from '@/components/CaptureAvatar';
import { LetterStamp, PAPER_IS_CHROME } from '@/components/letter/LetterStamp';
import { Pigeon } from '@/components/Pigeon';
import {
  CardFields,
  cardIsComplete,
  cardProblems,
  useHandleAvailability,
  type CardDraft,
  type CardProblems,
} from '@/components/profile/CardFields';
import { Button } from '@/components/ui/button';
import { ApiError, finishOnboarding } from '@/lib/api';
import { initials, monogramStyle } from '@/lib/avatar';
import { forgetInvite, recallInvite } from '@/lib/invite';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';

/**
 * The column every step is drawn in.
 *
 * ONE STRING BECAUSE THE STEPS HAD THREE COPIES OF IT, and copies drift: the form
 * scrolled and the photograph step did not, so the two screens sat at different
 * measures on a wide phone and the button on one was a different distance from the
 * bottom edge than the button on the other. A signup is the one flow where every
 * screen is seen back to back, which is where that shows.
 *
 * `max-w-sm` AND CENTRED, matching the sign-in screen this flow arrives from, so the
 * column does not change width at the moment somebody signs in. `h-full` because the
 * gate hands each step a definite height (see `AppShell`), and that is what puts a
 * button at the bottom of the phone rather than under the fold.
 */
const COLUMN = 'mx-auto flex h-full w-full max-w-sm flex-col px-6 pt-14 pb-8';

/** The step's own button — the sign-in screen's pill, so the flow does not change
 *  its control language halfway through. */
const PRIMARY = 'h-11 w-full rounded-full text-[15px]';

/** And the way past, which is that button gone quiet. Same size, same shape, no
 *  ground: see the note where it is first used. */
const NOT_NOW = `${PRIMARY} text-muted-foreground`;

/**
 * The stamp on the step before the camera, and the fraction of it its initials are.
 *
 * 116px BECAUSE THE CAMERA'S FRAME IS 160 AND THIS ONE IS NOT THE SUBJECT. It is
 * what the choice is about, above a heading and a sentence, on a screen that has to
 * fit all three on a 500px viewport with two buttons under them.
 */
const STAMP = 116;
const STAMP_INITIALS = 0.325;

/**
 * Setting up an account, which is where an account starts existing
 * (`POST /me/onboarding` — before it the row is `onboarding`: unpostable,
 * unfollowable, unfindable).
 *
 * THE CARD IS CLAIMED FIRST AND THE PHOTOGRAPH LAST, AND THE ORDER IS LOAD-BEARING.
 * The first step is the one that can be REFUSED: a taken handle is a 409 and an
 * under-18 date of birth is a 422, and both have to land on the form that produced
 * them. Photographing yourself and then being told your handle is gone would be the
 * product taking a picture it had already decided not to keep.
 *
 * BETWEEN THEM IS A SCREEN THAT ASKS FOR THE CAMERA BEFORE ANYTHING OPENS IT
 * (docs/5 § D260808-1713). `CaptureAvatar` starts a stream on mount, so arriving straight
 * on it raised the permission sheet as the first thing on screen — a system prompt
 * with no question of ours behind it, which is the arrangement people refuse, and a
 * refusal here used to look like a dead screen. So the step is: the stamp the person
 * will wear, one line saying what a photograph here is, *Continue* and *Not now*.
 *
 * THE ONE LINE IS THE ONE PIECE OF PROSE IN THE PRODUCT, and it is on this screen
 * rather than under a control (docs/1 § Copy in the interface allows exactly this:
 * onboarding is a separate surface, and explanation belongs on one). It is a
 * sentence, it is never on a working surface, and nothing else on any step joins it.
 *
 * THE FIELDS THEMSELVES ARE NOT HERE (docs/5 § D260805-0242). They are `CardFields`, shared
 * with the edit form, because a signup form and an edit form that each own a copy of
 * "what a card is" are two forms that disagree about it — see that file for which two
 * fields differ between them and why. What this screen owns is the three steps, the
 * request, and the refusals.
 *
 * WHAT IS COLLECTED, AND NOTHING ELSE IS: handle, name, date of birth, and
 * optionally a city and a bio. No gender, no interests, no religion, no "what are you
 * looking for" — docs/5 § D260803-1552 took the dating panel off the card entirely, and a
 * signup form is where that sort of thing walks back in.
 *
 * NOBODY IS ASKED TO NAME THEIR BIRD HERE. It already has a name (docs/5 § D260805-0242) and
 * renaming it is on the edit form: a question at minute two about an object the
 * person has not met yet is a question they answer with a placeholder.
 *
 * THE DATE OF BIRTH IS THE 18+ GATE (docs/8 § A) AND IT IS NOT AN AGE. It is sent,
 * never displayed back, and nothing in the product renders a number from it. The
 * field says what it is and does not explain what it is for.
 *
 * THE TIMEZONE IS DETECTED AND SENT WITHOUT ASKING. It decides which dawn a letter
 * flies on (`sweep` runs hourly precisely because 08:00 local happens forty times a
 * day), so it is infrastructure rather than a preference — and a dropdown of 400 IANA
 * zones on a signup screen is a question with one right answer the device already
 * knows.
 *
 * NO PROGRESS BAR, NO "STEP 1 OF 3", NO SKIP LINK ON THE FIRST STEP. Three screens do
 * not need a chrome to count themselves, and the only thing that can be skipped is the
 * photograph — an account without one is an ordinary account and wears a monogram
 * (docs/5 § D260805-0422), which is what the middle step draws.
 *
 * THE WAY PAST IS NAMED ON BOTH PHOTOGRAPH STEPS, AND THAT IS THE FIX (docs/5 §
 * D260808-1713). It used to be one button labelled *Done* on a screen where nothing was
 * done, which reads as a dead end to somebody whose camera will not open — the state
 * this flow produces most. It says *Not now* until there is a photograph and *Done*
 * once there is, so the button describes what pressing it does in both states, and
 * neither one asks anybody to decline the same thing twice.
 */
export function OnboardingScreen() {
  const { suggestedHandle, suggestedName, suggestedCity, refresh } = useSession();

  const [step, setStep] = useState<'card' | 'ready' | 'photograph'>('card');
  /**
   * Whether a photograph reached the server, which only the parent can hold:
   * `CaptureAvatar` uploads the moment the shutter is pressed and keeps its own
   * `done` state, and a retake drops that state while the account keeps the
   * photograph. So this latches — it is "there is one on the account", which is
   * what the button below is describing.
   */
  const [captured, setCaptured] = useState(false);
  const [draft, setDraft] = useState<CardDraft>({
    handle: suggestedHandle ?? '',
    name: suggestedName ?? '',
    dob: '',
    /*
     * THE CITY IS ALREADY FILLED IN, WITHOUT ANYBODY BEING ASKED FOR IT
     * (docs/5 § D260809-1521). The edge read it off the connection this session arrived
     * on, so the commonest path through this field is nought taps and no permission
     * prompt — and the control beside it corrects it from the device for anybody
     * whose network says the wrong thing.
     */
    city: suggestedCity ?? '',
    bio: '',
    birdName: '',
  });
  /**
   * A refusal the server made about one field, shown on that field.
   *
   * A TAKEN HANDLE IS NOT A FORM-LEVEL ERROR. It is one field's problem and it
   * belongs under that field, where the thing to change is; a sentence above the
   * button leaves somebody scanning five inputs for which one it meant. Cleared on
   * the next keystroke, because the answer to "that name is taken" is typing.
   */
  const [refused, setRefused] = useState<CardProblems>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);

  const handleState = useHandleAvailability(draft.handle);
  const problems: CardProblems = { ...cardProblems(draft, 'create'), ...refused };
  const canClaim =
    cardIsComplete(draft, 'create') &&
    !Object.keys(problems).length &&
    handleState.kind !== 'taken' &&
    !busy;

  const change = (patch: Partial<CardDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    /* Any edit clears the server's verdict on the field that was edited: it was a
       verdict on a value that no longer exists. */
    setRefused((r) => {
      const next = { ...r };
      for (const key of Object.keys(patch)) delete next[key as keyof CardDraft];
      return next;
    });
    setError(null);
  };

  const claim = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canClaim) return;
    setBusy(true);
    setError(null);
    const invitation = recallInvite();
    try {
      await finishOnboarding({
        handle: draft.handle,
        displayName: draft.name.trim(),
        dob: draft.dob,
        ...(draft.city.trim() ? { city: draft.city.trim() } : {}),
        ...(draft.bio.trim() ? { bio: draft.bio.trim() } : {}),
        /* Asked of the device, not of the person. */
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        /*
         * THE INVITATION, SPENT HERE BECAUSE THIS IS WHERE THE ACCOUNT STARTS
         * EXISTING (docs/5 § D260805-0328). It was written to the device when a `/join/…`
         * link was opened, possibly before an OAuth round trip that left the app
         * — see `lib/invite` for why it cannot travel any other way.
         *
         * NOTHING ON THIS SCREEN MENTIONS IT. No "invited by Noor" line, no
         * badge, no confirmation: the person read that sentence on the screen
         * that brought them here, and repeating it on a form is the product
         * narrating itself. The server ignores a code it cannot resolve, so
         * there is also nothing here that can go wrong for them to be told
         * about.
         */
        ...(invitation ? { inviteCode: invitation } : {}),
      });
      /* ONLY AFTER IT SUCCEEDED. Clearing on the attempt would lose a real
         invitation to one flaky request, and the claim is idempotent server-side,
         so a retry that carries it again is free. */
      forgetInvite();
      setStep('ready');
    } catch (err) {
      /* The server's own sentence: "That name is taken.", "Dove is for adults.",
         "Use 3–20 characters…". Every one of them is written to be read, and
         paraphrasing them here would be two copies of the same rule. */
      const message =
        err instanceof ApiError ? err.message : 'That didn’t work.';
      /* A conflict is always the handle — it is the only unique thing on the form —
         so it lands on that field rather than above the button, and the focus goes
         with it. Without the focus move, somebody using a screen reader is told the
         form was refused and left standing at the button. */
      if (err instanceof ApiError && err.code === 'conflict') {
        setRefused({ handle: message });
        requestAnimationFrame(() =>
          form.current
            ?.querySelector<HTMLElement>('input[autocomplete="username"]')
            ?.focus(),
        );
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  /* The account exists from the moment the card is claimed, so this is what lets the
     app in — the gate re-reads `/me` and finds an active account. */
  const finish = () => void refresh();

  if (step === 'ready') {
    return (
      <div className={COLUMN}>
        <div className="flex flex-1 flex-col items-center justify-center gap-7 text-center">
          {/*
            THE STAMP THEY ARE ALREADY WEARING, WITH THEIR OWN INITIALS AND THEIR
            OWN HUE (`monogramStyle`, keyed off the handle they have just claimed).

            It is the object under discussion rather than an illustration of one: a
            photograph taken on the next screen is printed in this frame, and an
            account that never takes one keeps exactly what is on screen here
            (docs/5 § D260805-0422). So the picture makes the choice concrete and
            costs the copy a sentence — nothing has to say what the photograph is
            *for* or where it ends up, because it is being worn in front of them.

            LEFT AT ITS NATURAL TILT, unlike the camera's frame: this is a stamp
            lying on a card and not a viewfinder anybody is aligning a face in.
            `PAPER_IS_CHROME` because a signup screen is app ground — see
            `LetterStamp`.
          */}
          <LetterStamp
            width={STAMP}
            style={PAPER_IS_CHROME}
            portrait={
              <span
                className="monogram flex size-full items-center justify-center font-medium"
                /* `STAMP_INITIALS` for the reason `LetterStamp` gives where it draws
                   the same monogram: two initials sized in points do not fit a
                   printed area that is worn from 26px to 160px, so the face is a
                   fraction of the stamp. Same fraction, so this is the same object
                   at a different size rather than a second monogram. */
                style={{
                  ...monogramStyle(draft.handle),
                  fontSize: STAMP * STAMP_INITIALS,
                }}
              >
                {initials(draft.name)}
              </span>
            }
          />

          <div className="flex flex-col gap-2.5">
            <h1 className="text-[26px] leading-tight font-bold tracking-tight">
              Your photograph
            </h1>
            {/* THE ONE SENTENCE. See the note at the top of this file for why it is
                allowed to be here and nowhere else. `text-balance` so it breaks into
                two even lines instead of one long one and an orphan. */}
            <p className="text-[15px] leading-normal text-balance text-muted-foreground">
              Everybody here shows up as themselves — one selfie, taken now,
              unedited.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Button
            size="lg"
            className={PRIMARY}
            onClick={() => setStep('photograph')}
          >
            Continue
          </Button>
          {/*
            THE WAY PAST, SAID OUT LOUD AND BEFORE THE CAMERA IS EVER ASKED FOR.

            Ghost rather than outline, and at the primary's own size: it is the
            quieter of two REAL choices rather than a second call to action or a
            footnote, and a person who has decided not to do this should not have to
            aim at a smaller target than the person who has. Muted is what makes it
            quiet; the geometry is what makes it a choice. `NOT_NOW` because the
            photograph step below wears the same control for the same reason.
          */}
          <Button variant="ghost" className={NOT_NOW} onClick={finish}>
            Not now
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'photograph') {
    return (
      <div className={COLUMN}>
        <h1 className="text-[26px] leading-tight font-bold tracking-tight">
          Your photograph
        </h1>

        <div className="flex flex-1 items-center justify-center py-8">
          <CaptureAvatar onCaptured={() => setCaptured(true)} />
        </div>

        {/* ONE BUTTON, WHICH IS ALSO THE WAY PAST — and which says which of the two it
            is. Somebody who took a photograph and somebody whose camera would not
            open are both finished with this screen, so a separate *Skip* would ask
            the second person to decline something twice; a *Done* that never changed
            told them the screen had nothing else to offer. */}
        <Button
          size="lg"
          variant={captured ? 'default' : 'ghost'}
          className={captured ? PRIMARY : NOT_NOW}
          onClick={finish}
        >
          {captured ? 'Done' : 'Not now'}
        </Button>
      </div>
    );
  }

  return (
    /*
     * THE FIELDS SCROLL AND THE BUTTON DOES NOT.
     *
     * The form used to be one scrolling column with the button pushed to the end of
     * it by `mt-auto`, which is fine on a tall phone with the keyboard down and is
     * the whole problem on any other: five fields plus a soft keyboard is taller
     * than the viewport, so *Continue* left the screen exactly when somebody
     * finished typing, and the way to reach it was to dismiss the keyboard first.
     * The scroll area is now the fields alone and the button is a sibling of it, so
     * the submit is on screen for the whole form — which is the same rule the tab
     * bar and every other control in this app follows (CLAUDE.md).
     */
    <form
      ref={form}
      onSubmit={claim}
      /* The column's own padding is redistributed: the scroll area carries the sides
         and the top, the footer carries the sides and the bottom. Otherwise the
         fields scroll under a padded edge instead of under the button. */
      className={cn(COLUMN, 'px-0 pt-0 pb-0')}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-14 pb-2">
        {/*
          `my-auto` RATHER THAN A TOP-ALIGNED STACK, and it is the one line that makes
          this screen look composed on a tall phone. Five fields is about two thirds of
          a modern viewport, so aligned to the top they left a hand's width of nothing
          between the bio and the button — the form read as though it had been cut off.
          Auto margins centre the group when it fits and COLLAPSE TO ZERO when it does
          not, so a short screen (or a raised keyboard) still scrolls from the first
          field. `justify-center` on the scroll container is the version of this that
          looks identical and clips the top of the content once it overflows.
        */}
        <div className="my-auto flex flex-col gap-4">
          {/* THE MARK, SMALL, BESIDE THE TITLE. The sign-in screen this arrives from
              floats it large in the space above its form; here there is no space to
              float anything in, and a signup that dropped the bird entirely read as a
              different product's form. It shrinks into the corner as the flow goes on,
              which is the continuity — at 32px it is letterhead rather than a logo. */}
          <div className="flex items-center gap-2.5">
            <Pigeon aria-hidden className="w-8 shrink-0" />
            <h1 className="text-[26px] leading-tight font-bold tracking-tight">
              Your card
            </h1>
          </div>

          <CardFields
            mode="create"
            draft={draft}
            onChange={change}
            problems={problems}
            handleState={handleState}
          />

          {error && (
            /* Under the fields, in the flow, so the button does not move when it
               appears and disappears. `role="alert"` because somebody who cannot see
               it still has to be told the form was refused. */
            <p role="alert" className="px-1 text-[13px] text-destructive">
              {error}
            </p>
          )}
        </div>
      </div>

      {/* The last field fades into the footer instead of being sliced off by it.
          Negative margin so the fade overlaps the scroll area rather than costing
          it height, and `pointer-events-none` so it does not eat the touch that
          scrolls the field underneath. */}
      <div
        aria-hidden
        className="pointer-events-none -mt-8 h-8 shrink-0 bg-gradient-to-b from-transparent to-background"
      />
      <div className="shrink-0 px-6 pt-2 pb-8">
        <Button
          type="submit"
          size="lg"
          className={PRIMARY}
          disabled={!canClaim}
        >
          Continue
        </Button>
      </div>
    </form>
  );
}
